import React, { useEffect, useState } from 'react';
import { Row, Col, FormInput, FormRadioGroup } from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import styles from '../PlantItemDetails.module.scss';
import { RefData } from 'plantItemNew/constants/refData';
import { PlantItemDetailsPage } from 'plantItemNew/constants/plantItemDetailsPage';

export const BoilerDetails = React.forwardRef((props, ref) => {
  const { value = {}, onChange = () => {}, refData = {}, onErrorChange = () => {} } = props;
  const {
    typeOfBoiler: savedTypeOfBoiler = '',
    otherTypeOfBoiler: savedOtherTypeOfBoiler = '',
    hazardLevel: savedHazardLevel = '',
    designPressure: savedDesignPressure = '',
    designTemperature: savedDesignTemperature = '',
    maxVolume: savedMaxVolume = '',
    outputUnit: savedOutputUnit = '',
    outputValue: savedOutputValue = '',
    attendance: savedAttendance = '',
  } = value;
  const [typeOfBoiler, setTypeOfBoiler] = useState(savedTypeOfBoiler);
  const [otherTypeOfBoiler, setOtherTypeOfBoiler] = useState(savedOtherTypeOfBoiler);

  const [hazardLevel, setHazardLevel] = useState(savedHazardLevel);
  const [designPressure, setDesignPressure] = useState(savedDesignPressure);
  const [maxVolume, setMaxVolume] = useState(savedMaxVolume);
  const [designTemperature, setDesignTemperature] = useState(savedDesignTemperature);
  const [outputUnit, setOutputUnit] = useState(savedOutputUnit);
  const [outputValue, setOutputValue] = useState(savedOutputValue);
  const [attendance, setAttendance] = useState(savedAttendance);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const validators = [
    {
      fieldName: 'hazardLevel',
      fieldValue: hazardLevel,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'designPressure',
      fieldValue: designPressure,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'designTemperature',
      fieldValue: designTemperature,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxVolume',
      fieldValue: maxVolume,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'outputUnit',
      fieldValue: outputUnit,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'outputValue',
      fieldValue: outputValue,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'attendance',
      fieldValue: attendance,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
  ];

  const hazardLevelOptions = Array.isArray(refData[RefData.boiler.hazardLevelOptions])
    ? refData[RefData.boiler.hazardLevelOptions].map((e) => ({
        value: e.value,
        label: e.description,
      }))
    : [];

  const outputUnitOptions = Array.isArray(refData[RefData.boiler.outputUnitOptions])
    ? refData[RefData.boiler.outputUnitOptions].map((e) => ({
        value: e.value,
        label: e.description,
      }))
    : [];

  const attendanceOptions = Array.isArray(refData[RefData.boiler.attendanceOptions])
    ? refData[RefData.boiler.attendanceOptions].map((e) => ({
        value: e.value,
        label: e.description,
      }))
    : [];

  if (ref) {
    ref.current = {
      validate: () => {
        let isCurrentComponentValid = validateExt(validators, setErrors, setErrorSummary);

        setShowErrors(!isCurrentComponentValid);

        return isCurrentComponentValid;
      },
    };
  }

  useEffect(() => {
    let details = {
      hazardLevel,
      designPressure,
      maxVolume,
      designTemperature,
      outputUnit,
      outputValue,
      attendance,
    };
    onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [hazardLevel, designPressure, maxVolume, designTemperature, outputUnit, outputValue, attendance]);

  useEffect(() => {
    onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <Row>
        <Col span={6}>
          <FormRadioGroup
            id="hazardLevel"
            name="hazardLevel"
            legend={PlantItemDetailsPage.relatedDetails.boiler.hazardLevelLabel}
            helpMessage="Boiler hazard level according to AS 4343"
            options={hazardLevelOptions}
            vertical={true}
            onChange={setHazardLevel}
            value={hazardLevel}
            errorMessage={errors?.hazardLevel?.errorMessage}
            hasError={showErrors && errors?.hazardLevel?.invalid}
          />
        </Col>
      </Row>
      <Row style={{ marginTop: 20 }}>
        <Col span={4}>
          <FormInput
            id="designPressure"
            label={PlantItemDetailsPage.relatedDetails.boiler.designPressureLabel}
            helpMessage="Kpag = Kilo Pascal. Also referred to as KpA"
            suffix="kPag"
            onChange={(e, { value }) => setDesignPressure(value)}
            value={designPressure}
            errorMessage={errors?.designPressure?.errorMessage}
            hasError={showErrors && errors?.designPressure?.invalid}
            className={styles.inputWithSuffix}
          />
          <FormInput
            id="designTemperature"
            label={PlantItemDetailsPage.relatedDetails.boiler.designTemperatureLabel}
            suffix="&deg;C"
            onChange={(e, { value }) => setDesignTemperature(value)}
            value={designTemperature}
            errorMessage={errors?.designTemperature?.errorMessage}
            hasError={showErrors && errors?.designTemperature?.invalid}
            className={styles.inputWithSuffix}
          />
          <FormInput
            id="maxVolume"
            label={PlantItemDetailsPage.relatedDetails.boiler.maxVolumeLabel}
            suffix="L"
            onChange={(e, { value }) => setMaxVolume(value)}
            value={maxVolume}
            errorMessage={errors?.maxVolume?.errorMessage}
            hasError={showErrors && errors?.maxVolume?.invalid}
            className={styles.inputWithSuffix}
          />

          <FormRadioGroup
            id="outputUnit"
            name="outputUnit"
            legend={PlantItemDetailsPage.relatedDetails.boiler.outputUnitLabel}
            options={outputUnitOptions}
            onChange={setOutputUnit}
            value={outputUnit}
            errorMessage={errors?.outputUnit?.errorMessage}
            hasError={showErrors && errors?.outputUnit?.invalid}
          />
          {outputUnit && (
            <FormInput
              id="outputValue"
              suffix={outputUnit}
              onChange={(e, { value }) => setOutputValue(value)}
              value={outputValue}
              errorMessage={errors?.outputValue?.errorMessage}
              hasError={showErrors && errors?.outputValue?.invalid}
              className={styles.inputWithSuffix}
            />
          )}
        </Col>
      </Row>
      <Row style={{ marginTop: 20 }}>
        <Col span={6}>
          <FormRadioGroup
            id="attendance"
            name="attendance"
            legend={PlantItemDetailsPage.relatedDetails.boiler.attendanceLabel}
            helpMessage="According to AS 2593:2004"
            options={attendanceOptions}
            onChange={setAttendance}
            vertical={true}
            value={attendance}
            errorMessage={errors?.attendance?.errorMessage}
            hasError={showErrors && errors?.attendance?.invalid}
          />
        </Col>
      </Row>
    </>
  );
});

BoilerDetails.displayName = 'BoilerDetails';
